import React, {
  useState, useEffect, useContext,
} from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import format from 'string-template';
import { CardContent, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CoachSelect from '../../components/Leads/components/CoachSelect';
import ColoredHeader from '../../components/ColoredHeader';
import FirebaseContext from '../../../context/FirebaseContext';
import LoadingOverlay from '../../components/LoadingOverlay';
import { generateCoachProfile, getCoachProfile, updateCoachProfile } from '../../utils/aiCoachProfile';
import useToast from '../../hooks/useToast';
import {
  Title,
  OptionContainer,
  Container,
  CardTitle,
  StyledEditButton,
  CardHeader,
  CardContainer,
  SubTitle,
  GenerateButton,
  ButtonContainer,
  StyledRegenerateButton,
} from './styles';
import texts from './texts.json';
import EditCoachProfileModal from './EditCoachProfileModal';

const AICoachProfile = ({ role }) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const [selectedCoach, setSelectedCoach] = useState({});
  const [profileText, setProfileText] = useState('');
  const [isEditCoachProfile, setIsEditCoachProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!selectedCoach.id) {
      return;
    }
    setIsLoading(true);
    const fetchProfile = async () => {
      try {
        const profileMarkdown = await getCoachProfile(selectedCoach.id, remote);
        setProfileText(profileMarkdown);
      } catch (error) {
        showToast(error.message, { error: true });
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [selectedCoach.id, remote, showToast]);

  const handleProfileGeneration = async () => {
    if (!selectedCoach.id) {
      return;
    }
    setIsLoading(true);
    try {
      const profileMarkdown = await generateCoachProfile(selectedCoach.id, remote);
      setProfileText(profileMarkdown);
    } catch (error) {
      showToast(error.message, { error: true });
    }
    setIsLoading(false);
  };

  const handleProfileUpdate = async (newProfileText) => {
    if (!selectedCoach.id) {
      return;
    }
    setIsLoading(true);
    try {
      const profileMarkdown = await updateCoachProfile(selectedCoach.id, newProfileText, remote);
      setProfileText(profileMarkdown);
      showToast(texts.profileUpdated, { success: true });
    } catch (error) {
      showToast(error.message, { error: true });
    }
    setIsLoading(false);
    setIsEditCoachProfile(false);
  };

  let content;

  if (!selectedCoach.id && !isLoading) {
    content = <SubTitle>{texts.noCoachSelected}</SubTitle>;
  } else if (!profileText) {
    content = (
      <CardContainer>
        <SubTitle>{texts.noProfileText}</SubTitle>
        <GenerateButton variant="primary" fullWidth onClick={handleProfileGeneration}>
          <AutoAwesomeIcon />
          {texts.generateProfile}
        </GenerateButton>
      </CardContainer>
    );
  } else {
    content = (
      <CardContainer>
        <CardContent>
          <CardHeader>
            <CardTitle>{format(texts.profileTitle, { name: selectedCoach.label })}</CardTitle>
            <ButtonContainer>
              <Tooltip title={texts.reGenerateProfile}>
                <StyledRegenerateButton onClick={handleProfileGeneration} />
              </Tooltip>
              <StyledEditButton onClick={() => setIsEditCoachProfile(true)} />
            </ButtonContainer>
          </CardHeader>
          <ReactMarkdown>{profileText}</ReactMarkdown>
        </CardContent>
      </CardContainer>
    );
  }

  return (
    <Container>
      <ColoredHeader>
        <Title>{texts.title}</Title>
        <OptionContainer>
          <CoachSelect role={role} onChange={(coach) => setSelectedCoach(coach || {})} />
        </OptionContainer>
      </ColoredHeader>
      {isLoading ? null : (
        <>
          {content}
          <EditCoachProfileModal
            isOpen={isEditCoachProfile}
            onDidDismiss={() => setIsEditCoachProfile(false)}
            profileMarkdown={profileText}
            onUpdateProfile={handleProfileUpdate}
          />
        </>
      )}
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

AICoachProfile.propTypes = {
  role: PropTypes.string,
};

AICoachProfile.defaultProps = {
  role: '',
};

export default AICoachProfile;
